import { useMutation, useQueryClient } from '@tanstack/react-query';
import { scorecardKeys } from '../Scorecard/queryKeys';
import { CreateInvestmentInput, DeleteInvestmentInput, UpdateInvestmentInput } from '../generated/graphql';
import {
  clientCloseInvestment,
  CloseInvestmentInput,
  clientCreateInvestment,
  clientDeleteInvestment,
  clientUpdateInvestment,
  useGraphqlClient,
} from '../graphql';
import { investmentKeys } from './queryKeys';
import { closedAccountsQueryKey, ttDetailsKeys } from '../TtDetails';

export const useCreateInvestment = () => {
  const queryClient = useQueryClient();
  const gqlClient = useGraphqlClient();

  const createInvestment = useMutation({
    retry: 3,
    mutationKey: ['useCreateInvestment'],
    mutationFn: (input: CreateInvestmentInput) => clientCreateInvestment(gqlClient, input),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries(investmentKeys.investments(variables.householdID));
      queryClient.invalidateQueries(investmentKeys.investmentTypes(variables.householdID));
      queryClient.invalidateQueries(scorecardKeys.scorecard(variables.householdID));
    },
  });

  return { createInvestment };
};

export const useUpdateInvestment = () => {
  const queryClient = useQueryClient();
  const gqlClient = useGraphqlClient();

  const updateInvestment = useMutation({
    retry: 3,
    mutationKey: ['useUpdateInvestment'],
    mutationFn: (input: UpdateInvestmentInput) => clientUpdateInvestment(gqlClient, input),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries(investmentKeys.investments(variables.householdID));
      queryClient.invalidateQueries(investmentKeys.investmentTypes(variables.householdID));
      queryClient.invalidateQueries(scorecardKeys.scorecard(variables.householdID));
    },
  });

  return { updateInvestment };
};

export const useDeleteInvestment = () => {
  const queryClient = useQueryClient();
  const gqlClient = useGraphqlClient();

  const deleteInvestment = useMutation({
    retry: 3,
    mutationKey: ['useDeleteInvestment'],
    mutationFn: (input: DeleteInvestmentInput) => clientDeleteInvestment(gqlClient, input),
    onSuccess: async (_data, variables) => {
      queryClient.invalidateQueries(investmentKeys.investments(variables.householdID));
      queryClient.invalidateQueries(investmentKeys.investmentTypes(variables.householdID));
      queryClient.invalidateQueries(scorecardKeys.scorecard(variables.householdID));
      await queryClient.invalidateQueries(ttDetailsKeys.base);
    },
  });

  return { deleteInvestment };
};

export const useCloseInvestment = () => {
  const queryClient = useQueryClient();
  const gqlClient = useGraphqlClient();

  const closeInvestment = useMutation({
    mutationFn: (input: CloseInvestmentInput) => clientCloseInvestment(gqlClient, input),
    onSuccess: async (_data, variables) => {
      queryClient.invalidateQueries(investmentKeys.investments(variables.householdID));
      queryClient.invalidateQueries(investmentKeys.investmentTypes(variables.householdID));
      queryClient.invalidateQueries(scorecardKeys.scorecard(variables.householdID));
      queryClient.invalidateQueries(closedAccountsQueryKey(variables.householdID, 'Investment'));
      await queryClient.invalidateQueries(ttDetailsKeys.base);
    },
  });

  return { closeInvestment };
};
